import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Save, Upload, Palette, Type, Code as CodeIcon, Settings as SettingsIcon } from "lucide-react";

const DEFAULTS = {
  color_primary: "#FF4F00",
  color_secondary: "#00F0FF",
  color_accent: "#FFD700",
  color_bg: "#050505",
  color_text: "#FFFFFF",
  color_link: "#FF4F00",
  font_primary: "Rajdhani",
  font_secondary: "IBM Plex Sans",
  base_font_size: 14,
  custom_css: "",
  custom_js: "",
};

export default function IDGAppearanceTab() {
  const [data, setData] = useState(DEFAULTS);
  const [activeFile, setActiveFile] = useState("css");
  const [fileContent, setFileContent] = useState("");
  const [loadingFile, setLoadingFile] = useState(false);
  const [savingFile, setSavingFile] = useState(false);

  const loadFile = async (type) => {
    setLoadingFile(true);
    try {
      const { data: fileData } = await api.get(`/idg/design/files?filepath=${type}`);
      setFileContent(fileData.content);
      setActiveFile(type);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingFile(false);
    }
  };

  const saveFile = async () => {
    setSavingFile(true);
    try {
      await api.post(`/idg/design/files?filepath=${activeFile}`, { content: fileContent });
      // For CSS file: trigger instant live reload across all open pages
      if (activeFile === "css") {
        window.dispatchEvent(new Event("idg:theme-css-updated"));
        toast.success("✅ CSS salvat și aplicat instant pe site!");
      } else {
        toast.success("Fișier salvat pe server!");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingFile(false);
    }
  };

  useEffect(() => {
    api.get("/idg/appearance").then((r) => setData({ ...DEFAULTS, ...r.data }));
    loadFile("css");
  }, []);

  const upload = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data: u } = await api.post(`/upload?kind=${key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${u.url}`;
      const updated = { ...data, [`${key}_url`]: fullUrl };
      setData(updated);
      // Immediately persist the new URL to the database
      await api.patch("/idg/appearance", { [`${key}_url`]: fullUrl });
      window.dispatchEvent(new Event("idg:appearance-updated"));
      if (window.BroadcastChannel) {
        new BroadcastChannel("idg_sync").postMessage("appearance-updated");
      }
      toast.success("Încărcat și salvat!");
    } catch (err) { 
      toast.error("Eroare la încărcare: " + (err?.response?.data?.detail || err.message)); 
    }
    e.target.value = "";
  };

  const save = async () => {
    try {
      await api.patch("/idg/appearance", data);
      window.dispatchEvent(new Event("idg:appearance-updated"));
      if (window.BroadcastChannel) {
        new BroadcastChannel("idg_sync").postMessage("appearance-updated");
      }
      toast.success("✅ Aspect salvat și aplicat instant!");
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="idg-appearance-tab">
      <section className="tf-surface tf-clip-card p-5 space-y-3">
        <h3 className="tf-section-title flex items-center gap-2"><Upload className="w-3.5 h-3.5" /> Logo, Favicon & Background</h3>
        <div className="grid md:grid-cols-4 gap-4">
          <LogoSlot label="Logo (light mode)" url={data.logo_url} onUpload={(e) => upload(e, "logo")} onClear={() => setData({ ...data, logo_url: "" })} testid="logo-upload" />
          <LogoSlot label="Logo (dark mode)" url={data.logo_dark_url} onUpload={(e) => upload(e, "logo_dark")} onClear={() => setData({ ...data, logo_dark_url: "" })} testid="logo-dark-upload" />
          <LogoSlot label="Favicon" url={data.favicon_url} onUpload={(e) => upload(e, "favicon")} onClear={() => setData({ ...data, favicon_url: "" })} testid="favicon-upload" small />
          <LogoSlot label="Fundal (Background)" url={data.background_url} onUpload={(e) => upload(e, "background")} onClear={() => { setData({ ...data, background_url: "" }); api.patch("/idg/appearance", { background_url: null }).then(()=>window.dispatchEvent(new Event("idg:appearance-updated"))); }} testid="background-upload" />
        </div>
      </section>

      <section className="tf-surface tf-clip-card p-5 space-y-3">
        <h3 className="tf-section-title flex items-center gap-2"><Palette className="w-3.5 h-3.5" /> Culori</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <ColorField label="Primară" value={data.color_primary} onChange={(v) => setData({ ...data, color_primary: v })} testid="color-primary" />
          <ColorField label="Secundară" value={data.color_secondary} onChange={(v) => setData({ ...data, color_secondary: v })} testid="color-secondary" />
          <ColorField label="Accent" value={data.color_accent} onChange={(v) => setData({ ...data, color_accent: v })} testid="color-accent" />
          <ColorField label="Fundal" value={data.color_bg} onChange={(v) => setData({ ...data, color_bg: v })} testid="color-bg" />
          <ColorField label="Text" value={data.color_text} onChange={(v) => setData({ ...data, color_text: v })} testid="color-text" />
          <ColorField label="Link" value={data.color_link} onChange={(v) => setData({ ...data, color_link: v })} testid="color-link" />
        </div>
      </section>

      <section className="tf-surface tf-clip-card p-5 space-y-3">
        <h3 className="tf-section-title flex items-center gap-2"><Type className="w-3.5 h-3.5" /> Fonturi</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Font principal" value={data.font_primary} onChange={(v) => setData({ ...data, font_primary: v })} testid="font-primary" />
          <Field label="Font secundar" value={data.font_secondary} onChange={(v) => setData({ ...data, font_secondary: v })} testid="font-secondary" />
          <Field label="Mărime bază (px)" type="number" value={data.base_font_size} onChange={(v) => setData({ ...data, base_font_size: parseInt(v) || 14 })} testid="font-size" />
        </div>
      </section>

      <section className="tf-surface tf-clip-card p-5 space-y-3">
        <h3 className="tf-section-title flex items-center gap-2"><SettingsIcon className="w-3.5 h-3.5" /> Setări Header (Meniu)</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Field label="Slogan Header (Tagline)" value={data.header_tagline} onChange={(v) => setData({ ...data, header_tagline: v })} testid="header-tagline" />
          </div>
          <Field label="Facebook URL" value={data.header_social_facebook} onChange={(v) => setData({ ...data, header_social_facebook: v })} testid="header-social-fb" />
          <Field label="Twitter URL" value={data.header_social_twitter} onChange={(v) => setData({ ...data, header_social_twitter: v })} testid="header-social-tw" />
          <Field label="YouTube URL" value={data.header_social_youtube} onChange={(v) => setData({ ...data, header_social_youtube: v })} testid="header-social-yt" />
          <Field label="Instagram URL" value={data.header_social_instagram} onChange={(v) => setData({ ...data, header_social_instagram: v })} testid="header-social-ig" />
          <Field label="Twitch URL" value={data.header_social_twitch} onChange={(v) => setData({ ...data, header_social_twitch: v })} testid="header-social-twitch" />
          
          <div className="border-t border-white/5 md:col-span-2 my-2 pt-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Setări Logo Meniu</h4>
          </div>
          <div>
            <label className="text-xs tf-section-title block mb-1">Tip Logo Fallback (dacă nu e imagine)</label>
            <select 
              className="tf-input" 
              value={data.header_logo_type || "geometric"} 
              onChange={(e) => setData({ ...data, header_logo_type: e.target.value })}
              data-testid="header-logo-type"
            >
              <option value="geometric">Geometric (PlayerX Style)</option>
              <option value="text">Text Personalizat</option>
              <option value="none">Fără Logo Fallback</option>
            </select>
          </div>
          <Field label="Text Logo Fallback" value={data.header_logo_text} onChange={(v) => setData({ ...data, header_logo_text: v })} testid="header-logo-text" />
          <Field label="Înălțime Logo (px)" type="number" value={data.header_logo_height} onChange={(v) => setData({ ...data, header_logo_height: parseInt(v) || 48 })} testid="header-logo-height" />
          <Field label="Lățime Logo (px)" type="number" value={data.header_logo_width} onChange={(v) => setData({ ...data, header_logo_width: parseInt(v) || 160 })} testid="header-logo-width" />
          
          <div className="flex items-center justify-between tf-surface-elev tf-clip-card-sm px-3 py-2 cursor-pointer border border-white/5">
            <span className="text-xs text-zinc-200">Efect Laser Scanner (Logo Meniu)</span>
            <input type="checkbox" checked={!!data.header_laser_scanner} onChange={(e) => setData({ ...data, header_laser_scanner: e.target.checked })} className="accent-[var(--tf-primary)]" data-testid="header-laser-scanner" />
          </div>
        </div>
      </section>

      <section className="tf-surface tf-clip-card p-5 space-y-3">
        <h3 className="tf-section-title flex items-center gap-2"><CodeIcon className="w-3.5 h-3.5" /> Cod Personalizat (CSS / HTML / JS)</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs tf-section-title block mb-1">CSS Personalizat (Custom CSS)</label>
            <textarea 
              className="tf-input font-mono text-xs" 
              rows={6} 
              value={data.custom_css || ""} 
              onChange={(e) => setData({ ...data, custom_css: e.target.value })} 
              data-testid="custom-css"
              placeholder="/* Adaugă cod CSS aici */"
            />
          </div>
          <div>
            <label className="text-xs tf-section-title block mb-1">HTML / JS Personalizat (Body scripts)</label>
            <textarea 
              className="tf-input font-mono text-xs" 
              rows={6} 
              value={data.custom_js || ""} 
              onChange={(e) => setData({ ...data, custom_js: e.target.value })} 
              data-testid="custom-js"
              placeholder="<script>console.log('hello');</script>"
            />
          </div>
        </div>
      </section>

      <div className="flex gap-2">
        <button onClick={save} className="tf-btn-primary tf-clip-btn flex items-center gap-1" data-testid="idg-appearance-save"><Save className="w-3.5 h-3.5" /> Salvează aspect</button>
      </div>

      <section className="tf-surface tf-clip-card p-5 space-y-3">
        <div className="flex justify-between items-center border-b border-white/5 pb-3">
          <h3 className="tf-section-title flex items-center gap-2">
            <CodeIcon className="w-3.5 h-3.5 text-[var(--tf-primary)]" /> Editor Fișiere Temă
          </h3>
          <button
            onClick={saveFile}
            disabled={savingFile || loadingFile}
            className="tf-btn-primary tf-clip-btn flex items-center gap-1 text-xs"
            data-testid="idg-design-save"
          >
            <Save className="w-3 h-3" />
            {savingFile ? "Se salvează..." : "Salvează"}
          </button>
        </div>

        {/* Info banner diferentiat per tip fișier */}
        {activeFile === "css" ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded bg-green-500/10 border border-green-500/20 text-green-400 text-[11px]">
            <span className="font-bold">⚡ INSTANT</span> — Modificările la <code className="bg-black/30 px-1 rounded">index.css</code> se aplică <strong>imediat</strong> pe site fără refresh.
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 rounded bg-amber-500/10 border border-amber-500/20 text-amber-300 text-[11px]">
            <span className="font-bold">⚠️ ATENȚIE</span> — Fișierele <strong>JS / HTML</strong> sunt compilate în bundle. Modificările se salvează pe server, dar nu se văd pe site până la un <strong>rebuild manual</strong> (rulează scriptul <code className="bg-black/30 px-1 rounded">deploy_from_acp.ps1</code> de pe PC). Folosește secțiunea <strong>Aspect → Culori / Logo / CSS Personalizat</strong> pentru modificări instant.
          </div>
        )}

        {/* Tab-uri fișiere */}
        <div className="flex flex-wrap gap-1.5 pt-1">
          {[
            { label: "index.css ⚡", key: "css" },
            { label: "App.css", key: "app_css" },
            { label: "index.html", key: "html" },
            { label: "App.js ⚠️", key: "app_js" },
            { label: "Header.js ⚠️", key: "header_js" },
            { label: "Footer.js ⚠️", key: "footer_js" }
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => loadFile(f.key)}
              disabled={loadingFile || savingFile}
              className={`px-3 py-1.5 text-[10px] tf-heading font-bold uppercase tracking-widest transition-all tf-clip-card-sm border ${
                activeFile === f.key
                  ? "bg-white/10 text-white border-white/20"
                  : "text-zinc-500 border-transparent hover:text-zinc-300 hover:bg-white/5"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Textarea de editare */}
        <div className="bg-[#121212] rounded border border-white/5 overflow-hidden mt-3 relative">
          {loadingFile && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-zinc-400 text-xs tf-mono">
              Se încarcă fișierul...
            </div>
          )}
          <textarea
            value={fileContent}
            onChange={(e) => setFileContent(e.target.value)}
            disabled={loadingFile || savingFile}
            className="w-full h-[500px] p-4 bg-transparent text-zinc-300 font-mono text-xs resize-y outline-none focus:ring-1 focus:ring-[var(--tf-primary)] leading-relaxed"
            spellCheck="false"
          />
        </div>
      </section>

    </div>
  );
}

function LogoSlot({ label, url, onUpload, onClear, testid, small }) {
  return (
    <div>
      <label className="text-xs tf-section-title block mb-1">{label}</label>
      <div className={`tf-surface-elev tf-clip-card-sm border border-white/10 p-3 flex flex-col items-center gap-2 ${small ? "min-h-24" : "min-h-32"}`}>
        {url ? (
          <img src={url} alt={label} className={small ? "h-12" : "h-16"} />
        ) : (
          <div className={`flex items-center justify-center text-zinc-500 text-xs tf-mono ${small ? "h-12" : "h-16"}`}>Niciun fișier</div>
        )}
        <div className="flex gap-2">
          <label className="tf-btn-secondary tf-clip-btn text-[10px] cursor-pointer">
            <Upload className="inline w-3 h-3 mr-1" /> Upload
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid={testid} />
          </label>
          {url && <button onClick={onClear} className="text-[10px] px-2 py-1 text-red-400 hover:bg-red-500/10 tf-clip-card-sm">Șterge</button>}
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs tf-section-title block mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-12 h-10 tf-clip-card-sm bg-transparent border border-white/10" data-testid={testid} />
        <input type="text" className="tf-input flex-1 font-mono text-xs" value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs tf-section-title block mb-1">{label}</label>
      <input type={type} className="tf-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
